import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { AuthClient, LoginModel, UserDto } from '../../utils/apiClient';

// Define the Auth Context type
interface AuthContextType {
  user: UserDto | undefined;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
}

// Create the Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook for using the Auth Context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth Provider Component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto>();
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on initial render
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      if (parsed?.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${parsed.token}`;
      }
    }
    setLoading(false);
  }, []);

  // Login function
  const login = async (email: string, password: string) => {
    const authClient = new AuthClient(ApiUrl, axios);
    try {
      setLoading(true);
      const response = await authClient.login(new LoginModel({ email, password }));
      const userData = response;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      // Set token for future API calls
      axios.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData: any) => {
    const authClient = new AuthClient(ApiUrl, axios);
    try {
      setLoading(true);
      await authClient.register(userData);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setUser(undefined);
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Check if user has a specific role
  const hasRole = (role: string) => {
    return Boolean(user && user.roles?.includes(role));
  };

  // Check if user is an admin
  const isAdmin = () => {
    return hasRole('Админ');
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    hasRole,
    isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}